import { useState, useRef, useEffect } from "react";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useNotifications } from "@/context/NotificationsContext";

function relativeTime(iso) {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NotificationBell() {
    const { items, unread, markRead, markAllRead } = useNotifications();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="notification-bell"
                onClick={() => setOpen(o => !o)}
                className="btn-ghost !p-2 relative"
                aria-label="Notifications"
            >
                <Bell size={16} />
                {unread > 0 && (
                    <span
                        data-testid="notification-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[var(--terracotta)] text-white text-[10px] font-bold flex items-center justify-center px-1"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notifications-panel"
                    className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-white border border-[var(--border-soft)] rounded-2xl shadow-xl z-40 overflow-hidden"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-soft)]">
                        <div className="font-display font-semibold">Notifications</div>
                        {items.some(n => !n.read) && (
                            <button
                                data-testid="mark-all-read-btn"
                                onClick={markAllRead}
                                className="text-xs text-[var(--pine)] font-medium hover:underline"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="px-4 py-8 text-sm text-[var(--text-muted)] text-center">
                                You're all caught up.
                            </div>
                        ) : (
                            items.map(n => {
                                // holiday_booked notifications don't have a
                                // job — deep-link to the Team page so the
                                // manager can see the calendar bar.
                                const to = n.kind === "holiday_booked"
                                    ? "/team"
                                    : (n.job_id ? `/jobs/${n.job_id}` : "#");
                                return (
                                <Link
                                    key={n.id}
                                    to={to}
                                    data-testid={`notification-${n.id}`}
                                    onClick={() => { markRead(n.id); setOpen(false); }}
                                    className={`block px-4 py-3 border-b border-[var(--border-soft)] hover:bg-[var(--bg-subtle)] transition-colors ${
                                        !n.read ? "bg-[var(--pine-light)]/40" : ""
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {!n.read && (
                                            <span className="mt-1.5 h-2 w-2 rounded-full bg-[var(--terracotta)] shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm leading-snug">{n.message}</p>
                                            <p className="text-xs text-[var(--text-muted)] mt-1">
                                                {relativeTime(n.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
